import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, TrendUp, TrendDown, Trophy, Target, ChartBar, Minus } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TenderAnalytics() {
  const navigate = useNavigate();
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/tenders`).then(res => setTenders(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-20 text-center text-stone-400 text-sm">Loading analytics...</div>;

  // Summary metrics
  const total = tenders.length;
  const open = tenders.filter(t => t.status === 'Open').length;
  const awarded = tenders.filter(t => t.status === 'Awarded').length;
  const lost = tenders.filter(t => t.status === 'Lost').length;
  const submitted = tenders.filter(t => t.status === 'Submitted').length;
  const underReview = tenders.filter(t => t.status === 'Under Review').length;
  const cancelled = tenders.filter(t => t.status === 'Cancelled').length;
  const closed = tenders.filter(t => t.status === 'Closed').length;
  const decided = awarded + lost;
  const winRate = decided > 0 ? ((awarded / decided) * 100).toFixed(1) : '0.0';

  const totalQuoted = tenders.reduce((s, t) => s + (t.our_quoted_price || 0), 0);
  const totalWinning = tenders.filter(t => t.status === 'Awarded').reduce((s, t) => s + (t.winning_amount || 0), 0);

  // Status breakdown
  const statusData = [
    { label: 'Open', count: open, color: 'bg-blue-500' },
    { label: 'Submitted', count: submitted, color: 'bg-purple-500' },
    { label: 'Under Review', count: underReview, color: 'bg-amber-500' },
    { label: 'Awarded', count: awarded, color: 'bg-green-500' },
    { label: 'Lost', count: lost, color: 'bg-orange-500' },
    { label: 'Closed', count: closed, color: 'bg-stone-400' },
    { label: 'Cancelled', count: cancelled, color: 'bg-red-500' },
  ].filter(s => s.count > 0);

  // Monthly trend
  const monthlyMap = {};
  tenders.forEach(t => {
    if (!t.end_date) return;
    const month = t.end_date.slice(0, 7); // YYYY-MM
    if (!monthlyMap[month]) monthlyMap[month] = { total: 0, awarded: 0, lost: 0 };
    monthlyMap[month].total++;
    if (t.status === 'Awarded') monthlyMap[month].awarded++;
    if (t.status === 'Lost') monthlyMap[month].lost++;
  });
  const months = Object.keys(monthlyMap).sort();
  const maxMonthly = Math.max(...months.map(m => monthlyMap[m].total), 1);

  // Lost tender analysis — individual and overall % diff
  const lostTenders = tenders.filter(t => t.status === 'Lost' && t.our_quoted_price > 0 && t.winning_amount > 0);
  const lostWithDiff = lostTenders.map(t => {
    const diff = t.our_quoted_price - t.winning_amount;
    const pctDiff = ((diff / t.winning_amount) * 100).toFixed(1);
    return { ...t, diff, pctDiff: parseFloat(pctDiff) };
  });
  const overallOurTotal = lostTenders.reduce((s, t) => s + t.our_quoted_price, 0);
  const overallWinTotal = lostTenders.reduce((s, t) => s + t.winning_amount, 0);
  const overallPctDiff = overallWinTotal > 0 ? (((overallOurTotal - overallWinTotal) / overallWinTotal) * 100).toFixed(1) : '0.0';

  const fmt = (n) => n.toLocaleString('en-IN', { minimumFractionDigits: 2 });

  return (
    <div>
      <button onClick={() => navigate('/tenders')} data-testid="back-to-tenders"
        className="inline-flex items-center gap-1.5 text-stone-500 hover:text-stone-800 text-sm font-medium mb-6 transition-colors">
        <ArrowLeft size={16} /> Back to Tenders
      </button>

      <h1 className="text-3xl sm:text-4xl text-stone-900 mb-1" data-testid="analytics-title">Tender Analytics</h1>
      <p className="text-sm text-stone-500 mb-8">{total} total tenders</p>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card label="Total Tenders" value={total} icon={ChartBar} color="text-[#002FA7]" bg="bg-[#002FA7]/5" />
        <Card label="Open / Active" value={`${open + submitted + underReview}`} sub={`${open} open, ${submitted} submitted`} icon={Target} color="text-blue-600" bg="bg-blue-50" />
        <Card label="Won (Awarded)" value={awarded} sub={`Win rate: ${winRate}%`} icon={Trophy} color="text-green-600" bg="bg-green-50" />
        <Card label="Lost" value={lost} sub={decided > 0 ? `${(100 - parseFloat(winRate)).toFixed(1)}% loss rate` : ''} icon={TrendDown} color="text-orange-600" bg="bg-orange-50" />
      </div>

      {/* Value Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="bg-white border border-stone-200 p-5">
          <p className="text-xs font-bold uppercase tracking-wider text-stone-400 mb-1">Total Quoted Value</p>
          <p className="text-2xl font-bold text-stone-900" data-testid="total-quoted">Rs. {fmt(totalQuoted)}</p>
          <p className="text-xs text-stone-500 mt-1">Across all tenders with quoted price</p>
        </div>
        <div className="bg-white border border-stone-200 p-5">
          <p className="text-xs font-bold uppercase tracking-wider text-stone-400 mb-1">Total Won Value</p>
          <p className="text-2xl font-bold text-green-700" data-testid="total-won">Rs. {fmt(totalWinning)}</p>
          <p className="text-xs text-stone-500 mt-1">Total winning amount for awarded tenders</p>
        </div>
      </div>

      {/* Status Breakdown */}
      {statusData.length > 0 && (
        <div className="bg-white border border-stone-200 p-6 mb-8">
          <h2 className="text-xs font-bold uppercase tracking-wider text-stone-400 mb-5">Status Breakdown</h2>
          <div className="space-y-3">
            {statusData.map(s => (
              <div key={s.label} className="flex items-center gap-3">
                <span className="text-xs font-semibold text-stone-600 w-24 text-right">{s.label}</span>
                <div className="flex-1 bg-stone-100 h-7 relative">
                  <div className={`${s.color} h-full transition-all duration-500`}
                    style={{ width: `${Math.max((s.count / total) * 100, 2)}%` }} />
                </div>
                <span className="text-sm font-bold text-stone-800 w-10 text-right">{s.count}</span>
                <span className="text-xs text-stone-400 w-12 text-right">{((s.count / total) * 100).toFixed(0)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Monthly Trend */}
      {months.length > 0 && (
        <div className="bg-white border border-stone-200 p-6 mb-8">
          <h2 className="text-xs font-bold uppercase tracking-wider text-stone-400 mb-5">Monthly Trend</h2>
          <div className="space-y-2">
            {months.map(m => {
              const d = monthlyMap[m];
              const label = new Date(m + '-01').toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
              return (
                <div key={m} className="flex items-center gap-3">
                  <span className="text-xs font-semibold text-stone-600 w-20 text-right">{label}</span>
                  <div className="flex-1 bg-stone-100 h-6 relative flex">
                    {d.awarded > 0 && (
                      <div className="bg-green-500 h-full" style={{ width: `${(d.awarded / maxMonthly) * 100}%` }} />
                    )}
                    {d.lost > 0 && (
                      <div className="bg-orange-400 h-full" style={{ width: `${(d.lost / maxMonthly) * 100}%` }} />
                    )}
                    {(d.total - d.awarded - d.lost) > 0 && (
                      <div className="bg-blue-300 h-full" style={{ width: `${((d.total - d.awarded - d.lost) / maxMonthly) * 100}%` }} />
                    )}
                  </div>
                  <span className="text-sm font-bold text-stone-800 w-8 text-right">{d.total}</span>
                </div>
              );
            })}
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-stone-100">
              <span className="flex items-center gap-1.5 text-xs text-stone-500"><span className="w-3 h-3 bg-green-500 inline-block" /> Won</span>
              <span className="flex items-center gap-1.5 text-xs text-stone-500"><span className="w-3 h-3 bg-orange-400 inline-block" /> Lost</span>
              <span className="flex items-center gap-1.5 text-xs text-stone-500"><span className="w-3 h-3 bg-blue-300 inline-block" /> Other</span>
            </div>
          </div>
        </div>
      )}

      {/* Lost Tender Analysis */}
      {lostWithDiff.length > 0 && (
        <div className="bg-white border border-stone-200 p-6 mb-8">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-xs font-bold uppercase tracking-wider text-stone-400">Lost Tender Analysis</h2>
              <p className="text-xs text-stone-500 mt-1">Our quoted price vs winning amount for lost tenders</p>
            </div>
            <div className="text-right">
              <p className="text-xs font-bold uppercase tracking-wider text-stone-400">Overall Difference</p>
              <p className={`text-lg font-bold ${parseFloat(overallPctDiff) > 0 ? 'text-red-600' : 'text-green-600'}`}
                data-testid="overall-pct-diff">
                {parseFloat(overallPctDiff) > 0 ? '+' : ''}{overallPctDiff}%
              </p>
            </div>
          </div>

          <table className="w-full text-sm text-left">
            <thead>
              <tr className="border-b border-stone-200 bg-stone-50">
                <th className="py-2.5 px-4 font-semibold text-stone-500 text-xs uppercase tracking-wider">Item Type</th>
                <th className="py-2.5 px-4 font-semibold text-stone-500 text-xs uppercase tracking-wider">Bid No.</th>
                <th className="py-2.5 px-4 font-semibold text-stone-500 text-xs uppercase tracking-wider text-right">Our Quote</th>
                <th className="py-2.5 px-4 font-semibold text-stone-500 text-xs uppercase tracking-wider text-right">Winning Amt.</th>
                <th className="py-2.5 px-4 font-semibold text-stone-500 text-xs uppercase tracking-wider text-right">Difference</th>
                <th className="py-2.5 px-4 font-semibold text-stone-500 text-xs uppercase tracking-wider text-right">% Diff</th>
              </tr>
            </thead>
            <tbody>
              {lostWithDiff.map(t => (
                <tr key={t.id} className="border-b border-stone-100 hover:bg-stone-50/50">
                  <td className="py-3 px-4 font-semibold text-stone-900">{t.item_type}</td>
                  <td className="py-3 px-4 text-stone-600 text-xs font-mono">{t.bid_number || '\u2014'}</td>
                  <td className="py-3 px-4 text-right text-stone-700">Rs. {fmt(t.our_quoted_price)}</td>
                  <td className="py-3 px-4 text-right text-stone-700">Rs. {fmt(t.winning_amount)}</td>
                  <td className="py-3 px-4 text-right font-medium">
                    <span className={t.diff > 0 ? 'text-red-600' : 'text-green-600'}>
                      {t.diff > 0 ? '+' : ''}Rs. {fmt(Math.abs(t.diff))}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span className={`font-bold ${t.pctDiff > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {t.pctDiff > 0 ? '+' : ''}{t.pctDiff}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-stone-900 bg-stone-50">
                <td className="py-3 px-4 font-bold text-stone-900" colSpan="2">Overall Total</td>
                <td className="py-3 px-4 text-right font-bold text-stone-900">Rs. {fmt(overallOurTotal)}</td>
                <td className="py-3 px-4 text-right font-bold text-stone-900">Rs. {fmt(overallWinTotal)}</td>
                <td className="py-3 px-4 text-right font-bold">
                  <span className={overallOurTotal - overallWinTotal > 0 ? 'text-red-600' : 'text-green-600'}>
                    {overallOurTotal - overallWinTotal > 0 ? '+' : ''}Rs. {fmt(Math.abs(overallOurTotal - overallWinTotal))}
                  </span>
                </td>
                <td className="py-3 px-4 text-right">
                  <span className={`font-bold text-lg ${parseFloat(overallPctDiff) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {parseFloat(overallPctDiff) > 0 ? '+' : ''}{overallPctDiff}%
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {total === 0 && (
        <div className="bg-white border border-stone-200 p-10 text-center">
          <p className="text-stone-400 text-sm">No tender data available yet. Add tenders to see analytics.</p>
        </div>
      )}
    </div>
  );
}

function Card({ label, value, sub, icon: Icon, color, bg }) {
  return (
    <div className={`${bg} border border-stone-200 p-5`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold uppercase tracking-wider text-stone-400">{label}</p>
        <Icon size={20} className={color} weight="bold" />
      </div>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      {sub && <p className="text-xs text-stone-500 mt-1">{sub}</p>}
    </div>
  );
}
